import styled from "@emotion/styled";
import { Badge as BadgeOriginal } from "./element/Badge";
import CloudIcon from "img/cloud.svg?react";
import GearsIcon from "img/home/gears.svg?react";
import SwapIcon from "img/trade-history/swap.svg?react";
import HeartbeatIcon from "../../../img/heartbeat.svg?react";
import ExploreOnChainIcon from "../../../img/explore-on-chain.svg?react";
import StreamerImage3 from "../../../img/home/streamer-3.svg?react";
import StreamerImage4 from "../../../img/home/streamer-4.svg?react";
import { Title as TitleOriginal } from "./element/Title";
import { Button, ButtonBorderRadius } from "components/general/Button/Button";
import { Text } from "components/general/Text";
import { css } from "@emotion/react";
import { NAV_LINKS } from "config/consts";
import { MOBILE_WIDTH, useIsMobile } from "hooks/useDeviceType";
import { Spacer } from "components/general/Spacer/Spacer";
import { useRef } from "react";
import { motion, useInView } from "framer-motion";

export const FeatureApi = () => {
  const isMobile = useIsMobile();
  return (
    <Container>
      <GearsIcon
        css={css`
          width: 444px;
          @media (max-width: ${MOBILE_WIDTH}px) {
            width: 203px;
            height: 163px;
          }
        `}
      />
      <Badge icon={CloudIcon} text="API" />
      <MainContentContainer>
        <TextContainer>
          <Title>Power up your app with our robust APIs</Title>
          <Text
            textStyle="Paragraph"
            size="Mega"
            textCss={css`
              max-width: 1000px;
            `}
            shouldScale={true}
          >
            Bring the Bebop trading experience into your app with our versatile offering, seamless
            developer experience and flexible options for your revenue streams.
          </Text>
        </TextContainer>
        <Feature />
        <Button
          navigateTo={NAV_LINKS.DOCUMENTATION_API_INTRODUCTION}
          buttonType="tertiary"
          buttonCss={css`
            width: fit-content;
            padding: 22px;
            border-radius: ${ButtonBorderRadius.Mid};
          `}
        >
          Read API Docs
        </Button>
      </MainContentContainer>

      <div style={{ position: "absolute", bottom: isMobile ? 40 : 82, left: 0, zIndex: 0 }}>
        <StreamerImage3 style={isMobile ? { width: 160 } : {}} />
      </div>
      <div style={{ position: "absolute", top: isMobile ? -304 : -280, right: 0, zIndex: 0 }}>
        <StreamerImage4 {...(isMobile ? { width: 160 } : {})} />
      </div>
    </Container>
  );
};

// Sub-components

const Container = styled.section`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 100%;
`;

const MainContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 56px;
  align-items: center;
  justify-content: center;
  margin-bottom: 40px;
  width: 100%;

  @media (max-width: ${MOBILE_WIDTH}px) {
    gap: 40px;
  }
`;

const TextContainer = styled.div`
  width: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  @media (max-width: ${MOBILE_WIDTH}px) {
    width: 80%;
  }
`;

const Title = styled(TitleOriginal)`
  margin-bottom: 24px;
  width: 100%;
  max-width: 800px;
`;

const Badge = styled(BadgeOriginal)`
  margin-top: 38px;
  margin-bottom: 28px;
`;

const Feature = () => {
  const ref = useRef(null);
  const isVisible = useInView(ref);

  return (
    <FeatureContainer ref={ref}>
      {isVisible && (
        <>
          <div>
            <motion.div
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.3 }}
            >
              <SwapIcon key={"swap-icon"} width={48} height={48} />
            </motion.div>
            <Spacer height={16} />
            <motion.div
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.4 }}
            >
              <Text textStyle="Paragraph" size="XXL" shouldScale={true}>
                Powerful execution for single and multi-token trades
              </Text>
            </motion.div>
          </div>
          <div>
            <motion.div
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.5 }}
            >
              <HeartbeatIcon width={48} height={48} />
            </motion.div>
            <Spacer height={16} />
            <motion.div
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.6 }}
            >
              <Text textStyle="Paragraph" size="XXL" shouldScale={true}>
                Historical trades with rich data
              </Text>
            </motion.div>
          </div>
          <div>
            <motion.div
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.7 }}
            >
              <ExploreOnChainIcon width={44} height={44} />
            </motion.div>
            <Spacer height={16} />
            <motion.div
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.9 }}
            >
              <Text textStyle="Paragraph" size="XXL" shouldScale={true}>
                Valuable token metadata
              </Text>
            </motion.div>
          </div>
        </>
      )}
    </FeatureContainer>
  );
};

const FeatureContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  width: 70%;
  max-width: 768px;

  @media (max-width: ${MOBILE_WIDTH}px) {
    gap: 32px;
    grid-template-columns: auto;
    grid-template-rows: repeat(3, 1fr);
    width: 50%;
  }
`;
